import { isElectronApp, setPreventDisplaySleep } from "@/utils/electron"

type WakeLockRelease = () => void

type WakeLockSentinelLike = {
	released: boolean
	release: () => Promise<void>
	addEventListener?: (type: "release", listener: () => void) => void
}

let activeHolders = 0
let activeSentinel: WakeLockSentinelLike | null = null
let electronDisplaySleepPrevented = false
let visibilityListenerAttached = false

function isWakeLockSupported() {
	return typeof navigator !== "undefined" && "wakeLock" in navigator
}

async function acquireBrowserWakeLock() {
	if (typeof document === "undefined" || document.visibilityState !== "visible") {
		return
	}

	if (activeSentinel && !activeSentinel.released) {
		return
	}

	const wakeLock = navigator.wakeLock
	if (!wakeLock) {
		return
	}

	try {
		const sentinel = await wakeLock.request("screen")
		if (activeHolders <= 0) {
			await sentinel.release()
			console.log("[SleepResume][WakeLock] Browser wake lock released after stale acquire")
			return
		}

		activeSentinel = sentinel
		console.log("[SleepResume][WakeLock] Browser wake lock acquired")
		sentinel.addEventListener?.("release", () => {
			if (activeSentinel === sentinel) {
				activeSentinel = null
				console.log("[SleepResume][WakeLock] Browser wake lock released by platform")
			}
		})
	} catch (error) {
		console.warn("[DisplayWakeLock] Failed to acquire browser wake lock", error)
	}
}

async function releaseBrowserWakeLock() {
	if (!activeSentinel) {
		return
	}

	const sentinel = activeSentinel
	activeSentinel = null
	try {
		await sentinel.release()
		console.log("[SleepResume][WakeLock] Browser wake lock released")
	} catch (error) {
		console.warn("[DisplayWakeLock] Failed to release browser wake lock", error)
	}
}

async function setElectronDisplaySleepPrevention(enabled: boolean) {
	if (!isElectronApp || electronDisplaySleepPrevented === enabled) {
		return true
	}

	const preventDisplaySleepRequest = setPreventDisplaySleep(enabled)
	if (!preventDisplaySleepRequest) {
		console.warn(
			"[DisplayWakeLock] Electron display sleep prevention unavailable; falling back to browser wake lock",
		)
		return false
	}

	try {
		await preventDisplaySleepRequest
		if (enabled && activeHolders <= 0) {
			const releaseDisplaySleepPreventionRequest = setPreventDisplaySleep(false)
			if (releaseDisplaySleepPreventionRequest) {
				await releaseDisplaySleepPreventionRequest
			}
			electronDisplaySleepPrevented = false
			console.log(
				"[SleepResume][WakeLock] Electron prevent-display-sleep released after stale acquire",
			)
			return true
		}

		electronDisplaySleepPrevented = enabled
		console.log(`[SleepResume][WakeLock] Electron prevent-display-sleep set to ${enabled}`)
		return true
	} catch (error) {
		console.warn("[DisplayWakeLock] Failed to set Electron display sleep prevention", error)
		return false
	}
}

function ensureVisibilityListener() {
	if (visibilityListenerAttached || typeof document === "undefined") {
		return
	}

	document.addEventListener("visibilitychange", () => {
		if (activeHolders <= 0 || !isWakeLockSupported()) {
			return
		}

		if (document.visibilityState === "visible") {
			void acquireBrowserWakeLock()
			return
		}

		void releaseBrowserWakeLock()
	})

	visibilityListenerAttached = true
}

async function syncWakeLock() {
	const shouldPreventSleep = activeHolders > 0

	if (isElectronApp) {
		const didUseElectronSleepPrevention =
			await setElectronDisplaySleepPrevention(shouldPreventSleep)
		if (didUseElectronSleepPrevention) {
			return
		}
	}

	if (!isWakeLockSupported()) {
		return
	}

	ensureVisibilityListener()
	if (shouldPreventSleep) {
		await acquireBrowserWakeLock()
		return
	}

	await releaseBrowserWakeLock()
}

export function holdDisplayWakeLock(): WakeLockRelease {
	activeHolders += 1
	console.log(`[SleepResume][WakeLock] holdDisplayWakeLock acquired; holders=${activeHolders}`)
	void syncWakeLock()

	let released = false
	return () => {
		if (released) {
			return
		}
		released = true

		activeHolders = Math.max(0, activeHolders - 1)
		console.log(
			`[SleepResume][WakeLock] holdDisplayWakeLock released; holders=${activeHolders}`,
		)
		void syncWakeLock()
	}
}
